import { useInfiniteQuery, useMutation, type InfiniteData } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useWindowVirtualizer } from '@tanstack/react-virtual'
import { useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import { Box, Button, Copy, cx, Hash, Rows } from 'regen-ui'
import { TextMorph } from 'torph/react'
import { useChainId } from 'wagmi'
import { tempo } from 'wagmi/chains'
import Check from '~icons/lucide/check'
import Timer from '~icons/lucide/timer'
import X from '~icons/lucide/x'

import { api } from '#/lib/api.js'
import * as Auth from '#/lib/auth.js'
import { useDialogConnector } from '#/lib/connectors.js'
import { formatCreditCents, useCreditsBalance } from '#/lib/credits.js'
import { useFundingOptions } from '#/lib/funding-options.js'
import { ScreenLayout } from '#/routes/_app/-components/ScreenLayout.js'

/** Credits activity page for the current signed-in user. */
export const Route = createFileRoute('/_app/credits/activity')({
  component: CreditsActivityPage,
})

const relative = new Intl.RelativeTimeFormat('en', { numeric: 'auto' })
const rowHeight = 64

type CreditActivityItem = {
  amountCents: number
  createdAt: string
  currency: string
  direction: 'in' | 'out'
  id: string
  passkeyAddress?: string | null
  status: 'fail' | 'pending' | 'success'
  toAddress: string
  txId: string | null
  updatedAt: string
  walletAddress: string
}

type CreditActivityPage = {
  items: CreditActivityItem[]
  nextCursor: string | null
}

function CreditsActivityPage() {
  const address = Auth.useSessionAddress()
  const chainId = useChainId()
  const fundingOptions = useFundingOptions()
  const enabled = !!address && chainId === tempo.id && fundingOptions.data?.coinflow === true
  const balance = useCreditsBalance({ enabled })
  const activity = useCreditsActivity({ enabled })
  const items = useMemo(() => activity.data?.pages.flatMap((p) => p.items) ?? [], [activity.data])

  const count = items.length
  const listRef = useRef<HTMLDivElement>(null)
  const listOffsetRef = useRef(0)

  useLayoutEffect(() => {
    listOffsetRef.current = listRef.current?.offsetTop ?? 0
  }, [])

  const virtualizer = useWindowVirtualizer({
    count,
    estimateSize: () => rowHeight,
    overscan: 5,
    scrollMargin: listOffsetRef.current,
  })

  const fetchNextPage = activity.fetchNextPage
  const hasNextPage = activity.hasNextPage
  const isFetchingNextPage = activity.isFetchingNextPage

  useEffect(() => {
    const rows = virtualizer.getVirtualItems()
    const last = rows[rows.length - 1]
    if (!last) return
    if (last.index >= items.length - 5 && hasNextPage && !isFetchingNextPage) fetchNextPage()
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, items.length, virtualizer.getVirtualItems()])

  return (
    <ScreenLayout
      actions={enabled ? <BuyCreditsButton /> : null}
      description={
        <p className="text-copy-15 text-foreground-secondary">
          {address && enabled
            ? 'Available Credits for your signed-in wallet account.'
            : 'Purchases of Credits for your signed-in wallet account.'}
        </p>
      }
      heading={
        <>
          <h1 className="text-heading-24">Credits</h1>
          {address && enabled && <CreditBalanceAmount balance={balance} />}
        </>
      }
      walletActions={false}
    >
      <Box
        className="flex flex-col max-sm:-mx-4 sm:-mx-8 md:mx-0 max-md:rounded-none max-md:border-x-0"
        padding={0}
      >
        {!address ? (
          <p className="flex items-center justify-center py-12 text-label-15 text-foreground-secondary">
            Credits activity is available only for your signed-in account.
          </p>
        ) : fundingOptions.isLoading ? (
          <Rows variant="surface">
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </Rows>
        ) : !enabled ? (
          <p className="flex items-center justify-center py-12 text-label-15 text-foreground-secondary">
            Credits activity is not available for this account.
          </p>
        ) : activity.isLoading ? (
          <Rows variant="surface">
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </Rows>
        ) : activity.isError ? (
          <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
            <p className="text-label-15 text-negative">Failed to load Credits activity.</p>
            <Button onClick={() => void activity.refetch()} size="small" variant="secondary">
              Retry
            </Button>
          </div>
        ) : items.length === 0 ? (
          <p className="flex items-center justify-center py-12 text-label-15 text-foreground-secondary">
            No Credits activity yet
          </p>
        ) : (
          <Rows variant="surface">
            <div
              ref={listRef}
              className="relative w-full"
              style={{ height: virtualizer.getTotalSize() }}
            >
              {virtualizer.getVirtualItems().map((row) => (
                <div
                  className="absolute left-0 top-0 w-full"
                  key={row.index}
                  style={{
                    height: row.size,
                    transform: `translateY(${row.start - virtualizer.options.scrollMargin}px)`,
                  }}
                >
                  <CreditActivityRow item={items[row.index]} />
                </div>
              ))}
            </div>
            {isFetchingNextPage && (
              <>
                <SkeletonRow />
                <SkeletonRow />
              </>
            )}
          </Rows>
        )}
      </Box>
    </ScreenLayout>
  )
}

function BuyCreditsButton() {
  const connector = useDialogConnector()
  const buyCredits = useMutation({
    mutationFn: async () => {
      const provider = await connector.getProvider()
      await provider.request({ method: 'wallet_deposit', params: [{ intent: 'credits' }] })
    },
  })

  return (
    <Button
      className="max-sm:w-full"
      loading={buyCredits.isPending}
      onClick={() => buyCredits.mutate()}
      size="medium"
      type="button"
      variant="primary"
    >
      Buy Credits
    </Button>
  )
}

function CreditBalanceAmount(props: { balance: ReturnType<typeof useCreditsBalance> }) {
  const { balance } = props
  const content = (() => {
    if (balance.isLoading)
      return (
        <span className="inline-block h-[1em] w-48 animate-pulse rounded-body bg-skeleton align-middle" />
      )
    if (balance.isError)
      return <span className="text-heading-24 text-foreground-secondary">Unavailable</span>
    return <TextMorph>{formatCreditCents(balance.data?.credits.cents ?? 0, 'USD')}</TextMorph>
  })()

  return <p className="text-heading-32 tabular-nums min-[880px]:text-heading-48">{content}</p>
}

function useCreditsActivity(options: { enabled: boolean }) {
  const address = Auth.useSessionAddress()
  return useInfiniteQuery<
    CreditActivityPage,
    Error,
    InfiniteData<CreditActivityPage>,
    readonly unknown[],
    string | undefined
  >({
    enabled: options.enabled,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const query: Record<string, string> = {}
      if (pageParam) query.cursor = pageParam
      const res = await api.api.coinflow.activity.$get({ query })
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: string } | null
        throw new Error(body?.error ?? 'Failed to load Credits activity.')
      }
      return (await res.json()) as CreditActivityPage
    },
    queryKey: Auth.session.queryKey(address, 'coinflow', 'activity'),
  })
}

function CreditActivityRow(props: { item: CreditActivityItem }) {
  const { item } = props
  const content = (
    <>
      <span
        className={cx(
          'flex size-6 shrink-0 items-center justify-center rounded-identicon bg-secondary text-foreground-secondary',
          'data-[status=success]:bg-positive-subtle data-[status=success]:text-positive',
          'data-[status=fail]:bg-negative-subtle data-[status=fail]:text-negative',
          item.txId &&
            'group-hover/rows-row:bg-secondary-hover group-hover/rows-row:text-foreground-secondary-hover group-active/rows-row:bg-secondary-active group-active/rows-row:text-foreground',
        )}
        data-status={item.status}
      >
        <StatusIcon className="size-3.5" status={item.status} />
      </span>
      <span className="flex flex-1 flex-col truncate text-left">
        <span className="text-label-14 text-foreground">{statusLabel(item)}</span>
        <span className="text-label-13 text-foreground-secondary">
          {item.txId ? (
            <CopyableHash value={item.txId} />
          ) : item.direction === 'out' ? (
            <>
              To <Hash value={item.toAddress} />
            </>
          ) : (
            <>
              Into <Hash value={item.walletAddress} />
            </>
          )}
        </span>
      </span>
      <span className="flex flex-col items-end">
        <span className="text-label-14 text-foreground tabular-nums">
          {formatCreditCents(item.amountCents, item.currency)}
        </span>
        <span className="text-label-14 text-foreground-secondary tabular-nums">
          {formatRelativeTime(item.updatedAt)}
        </span>
      </span>
    </>
  )

  if (item.txId) {
    return (
      <Rows.Row
        height={rowHeight}
        render={<a href={explorerUrl(item.txId)} rel="noopener noreferrer" target="_blank" />}
      >
        {content}
      </Rows.Row>
    )
  }

  return <Rows.Row height={rowHeight}>{content}</Rows.Row>
}

function explorerUrl(hash: string) {
  return `https://explore.tempo.xyz/receipt/${hash}`
}

function CopyableHash(props: { value: string }) {
  return (
    <span
      className="inline-flex items-center gap-1 text-foreground"
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
    >
      <Hash value={props.value} />
      <Copy size="xs" title="Copy transaction hash" value={props.value} />
    </span>
  )
}

function StatusIcon(props: {
  className?: string | undefined
  status: CreditActivityItem['status']
}) {
  switch (props.status) {
    case 'success':
      return <Check className={props.className} />
    case 'fail':
      return <X className={props.className} />
    case 'pending':
      return <Timer className={props.className} />
  }
}

function SkeletonRow() {
  return (
    <Rows.Row height={rowHeight}>
      <div className="size-6 animate-pulse rounded-identicon bg-skeleton" />
      <div className="flex flex-1 flex-col gap-1">
        <div className="h-3.5 w-24 animate-pulse rounded-body bg-skeleton" />
        <div className="h-3 w-32 animate-pulse rounded-body bg-skeleton" />
      </div>
      <div className="flex flex-col items-end gap-1">
        <div className="h-3.5 w-16 animate-pulse rounded-body bg-skeleton" />
        <div className="h-3 w-12 animate-pulse rounded-body bg-skeleton" />
      </div>
    </Rows.Row>
  )
}

function statusLabel(item: CreditActivityItem) {
  switch (item.status) {
    case 'pending':
      return item.direction === 'in' ? 'Credits purchase pending' : 'Credits redemption pending'
    case 'success':
      return item.direction === 'in' ? 'Credits purchased' : 'Credits redeemed'
    case 'fail':
      return item.direction === 'in' ? 'Credits purchase failed' : 'Credits redemption failed'
  }
}

function formatRelativeTime(value: string): string {
  const diff = new Date(value).getTime() - Date.now()
  const absDiff = Math.abs(diff)
  if (absDiff < 60_000) return 'just now'
  if (absDiff < 3_600_000) return relative.format(Math.round(diff / 60_000), 'minute')
  if (absDiff < 86_400_000) return relative.format(Math.round(diff / 3_600_000), 'hour')
  if (absDiff < 604_800_000) return relative.format(Math.round(diff / 86_400_000), 'day')
  return new Date(value).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}
